'use client';
import { useState } from 'react';
 import Icon from'@/components/ui/AppIcon';

interface Service {
  id: string
  title: string
  description: string
  icon: string
  features: string[]
}

export default function ServiceDetailCards() {
  const [expandedService, setExpandedService] = useState<string | null>(null)

  const services: Service[] = [
    {
      id: 'social_media_marketing',
      title: 'Social Media Marketing',
      description:
        'Comprehensive social media strategies tailored to your industry. From content creation to community management, we handle it all.',
      icon: 'ChatBubbleBottomCenterTextIcon',
      features: [
        'Platform-specific content strategies',
        'Daily posting and scheduling',
        'Community engagement and management',
        'Analytics and performance reporting',
        'Paid advertising campaigns',
        'Influencer collaboration',
      ],
    },
    {
      id: 'website_development',
      title: 'Website Development',
      description:
        'Modern, responsive websites built with the latest technologies. Fast, secure, and optimized for conversions.',
      icon: 'GlobeAltIcon',
      features: [
        'Custom responsive design',
        'E-commerce integration',
        'CMS implementation',
        'SEO optimization',
        'Performance optimization',
        'Ongoing maintenance and support',
      ],
    },
    {
      id: 'app_development',
      title: 'App Development',
      description:
        'Native and cross-platform mobile applications that deliver exceptional user experiences on iOS and Android.',
      icon: 'DevicePhoneMobileIcon',
      features: [
        'iOS and Android development',
        'Cross-platform solutions',
        'UI/UX design',
        'API integration',
        'App store deployment',
        'Post-launch support',
      ],
    },
    {
      id: 'graphic_design',
      title: 'Graphic Design',
      description:
        'Eye-catching visuals that elevate your brand identity. From logos to complete brand guidelines.',
      icon: 'SparklesIcon',
      features: [
        'Logo and brand identity',
        'Marketing collateral',
        'Social media graphics',
        'Infographics and illustrations',
        'Print design',
        'Brand style guides',
      ],
    },
    {
      id: 'festival_greetings',
      title: 'Festival Greetings Plan',
      description:
        'Year-long festival greeting designs to keep your brand present in customers\' minds during every celebration.',
      icon: 'GiftIcon',
      features: [
        'Custom festival designs',
        'Social media posts',
        'Email templates',
        'Print-ready formats',
        'Scheduling and planning',
        'Multi-platform delivery',
      ],
    },
    {
      id: 'wedding_invites',
      title: 'Wedding Invites',
      description:
        'Beautiful, personalized wedding invitation designs that capture your special day perfectly.',
      icon: 'HeartIcon',
      features: [
        'Custom invitation design',
        'Save-the-date cards',
        'RSVP cards',
        'Digital invitations',
        'Print-ready files',
        'Multiple design revisions',
      ],
    },
    {
      id: 'ecatalogue_brochures',
      title: 'E-Catalogue & Brochures',
      description:
        'Digital catalogues and brochures that showcase your products and services in the best light.',
      icon: 'DocumentTextIcon',
      features: [
        'Interactive digital catalogues',
        'Print brochure design',
        'Product photography',
        'Layout and typography',
        'Multi-page documents',
        'PDF and web formats',
      ],
    },
    {
      id: 'print_solutions',
      title: 'Print Solutions',
      description:
        'Professional printing services for all your business needs. From business cards to large-format banners.',
      icon: 'PrinterIcon',
      features: [
        'Business cards and stationery',
        'Brochures and flyers',
        'Banners and posters',
        'Packaging design',
        'Large-format printing',
        'Premium finishing options',
      ],
    },
    {
      id: 'product_shoots',
      title: 'Product Shoots',
      description:
        'Professional product photography that showcases your products in stunning detail and drives sales.',
      icon: 'CameraIcon',
      features: [
        'Studio product photography',
        'Lifestyle product shots',
        'E-commerce ready images',
        'Photo editing and retouching',
        '360-degree product views',
        'Multiple angle coverage',
      ],
    },
    {
      id: 'logo_designing',
      title: 'Logo Designing',
      description:
        'Unique, memorable logo designs that perfectly represent your brand identity and values.',
      icon: 'PaintBrushIcon',
      features: [
        'Custom logo concepts',
        'Multiple design revisions',
        'Vector file formats',
        'Brand color palette',
        'Typography selection',
        'Usage guidelines',
      ],
    },
    {
      id: 'whatsapp_bulk_marketing',
      title: 'WhatsApp Bulk Marketing',
      description:
        'Reach thousands of customers instantly with targeted WhatsApp campaigns and automated messaging.',
      icon: 'ChatBubbleLeftRightIcon',
      features: [
        'Bulk message campaigns',
        'Contact list management',
        'Rich media messaging',
        'Campaign analytics',
        'Automated responses',
        'Compliance management',
      ],
    },
    {
      id: 'voice_blast_marketing',
      title: 'Voice Blast Marketing',
      description:
        'Deliver pre-recorded voice messages to thousands of customers simultaneously for maximum reach.',
      icon: 'MegaphoneIcon',
      features: [
        'Automated voice campaigns',
        'Custom voice recording',
        'Scheduled broadcasts',
        'Call analytics and reports',
        'Multi-language support',
        'Contact segmentation',
      ],
    },
    {
      id: 'ivr_solutions',
      title: 'IVR Solutions',
      description:
        'Interactive Voice Response systems that streamline customer calls and improve service efficiency.',
      icon: 'PhoneIcon',
      features: [
        'Custom IVR menu design',
        'Multi-level call routing',
        'Voice recording services',
        'Call analytics dashboard',
        'Integration with CRM',
        '24/7 automated support',
      ],
    },
    {
      id: 'chatbot_ai_agents',
      title: 'Chatbot and AI Agents',
      description:
        'Intelligent AI-powered chatbots that provide instant customer support and automate conversations.',
      icon: 'CpuChipIcon',
      features: [
        'AI-powered conversations',
        'Multi-platform integration',
        'Natural language processing',
        'Lead qualification',
        'Analytics and insights',
        'Continuous learning',
      ],
    },
  ]

  return (
    <section className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-[2px] h-4 bg-amber-500" />
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-400">
              What We Offer
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
            Comprehensive Digital<br />Solutions
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            From strategy to execution, we provide end-to-end services for your digital needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, idx) => (
            <div
              key={service.id}
              className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all glow-hover reveal"
              style={{ transitionDelay: `${idx * 80}ms` }}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 flex items-center justify-center text-amber-500 bg-amber-500/10 rounded-xl">
                  <Icon name={service.icon as any} size={28} />
                </div>
                <button
                  onClick={() =>
                    setExpandedService(
                      expandedService === service.id ? null : service.id
                    )
                  }
                  className="text-zinc-400 hover:text-amber-500 transition-colors"
                  aria-label={expandedService === service.id ? 'Collapse' : 'Expand'}
                >
                  <Icon
                    name={
                      expandedService === service.id
                        ? 'ChevronUpIcon' :'ChevronDownIcon'
                    }
                    size={24}
                  />
                </button>
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">{service.title}</h3>
              <p className="text-zinc-400 leading-relaxed mb-6">
                {service.description}
              </p>

              {expandedService === service.id && (
                <div className="border-t border-zinc-800 pt-6 space-y-3 animate-slide-in-right">
                  <h4 className="text-sm font-bold text-amber-500 uppercase tracking-wider mb-4">
                    What's Included
                  </h4>
                  {service.features.map((feature, fIdx) => (
                    <div
                      key={fIdx}
                      className="flex items-start gap-3 text-sm text-zinc-400"
                    >
                      <Icon
                        name="CheckCircleIcon"
                        size={20}
                        className="text-amber-500 flex-shrink-0 mt-0.5"
                      />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}